import React, {useMemo, useState} from 'react';

import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

import {downloadDocumentFile} from 'api/documents';
import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {ReactComponent as DownloadIcon} from 'assets/svg/download2.svg';
import {HeaderLeft} from 'components/pages/DocumentsCreate/ConsumerReturn/ReturnInfo/styled';
import {Icon} from 'components/ui-kit/Icon';
import {
    CopyButton,
    Id,
    StatusWrapper,
    Title,
    TitleText,
    TitleWrapper,
} from 'components/ui-kit/PageComponents';
import {Stepper} from 'components/ui-kit/Stepper';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {colors} from 'theme';
import {BlockingReport} from 'types/documents';
import {BlockingScopes, BlockingSourceFormation} from 'types/products-blocking';
import {useClipboard} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {Products} from './Products';

import {
    AttachmentCard,
    AttachmentDownloadButton,
    AttachmentFileIcon,
    AttachmentFileMeta,
    AttachmentFileName,
    AttachmentsGrid,
    AttachmentsHeading,
    Error,
    ErrorsWrapper,
    GeneralInfoContent,
    InfoList,
    InfoListLabel,
    InfoListRow,
    InfoListValue,
    InfoListValueRow,
    InlineCopyButton,
} from './styled';

interface BlockingProps {
    data: BlockingReport;
}

const FILE_ICON_COLORS: Record<string, string> = {
    png: colors.brandPrimary,
    jpg: colors.brandPrimary,
    jpeg: colors.brandPrimary,
    xlsx: colors.success,
    xls: colors.success,
    pdf: colors.danger,
    docx: colors.brandDark,
    doc: colors.brandDark,
};

const getFileName = (attachment: string) => {
    const normalizedPath = attachment.replace(/\\/g, '/');

    return normalizedPath.split('/').pop() || attachment;
};

const getFileExtension = (fileName: string) => {
    const extension = fileName.split('.').pop();

    return extension && extension !== fileName ? extension.toLowerCase() : '';
};

export function convertData(data: BlockingReport, t: TFunction) {
    const reportInfo = data?.reportInfo;

    return {
        reportId: handleNil(data?.reportId),
        createdAt: handleNil(data?.createdAt, (value: string) =>
            formatDate(value, 'dd.MM.yyyy, HH:mm'),
        ),
        reportType: data?.reportType,
        status: handleNil(data?.status, (value: string) =>
            t(`dictionaries.documents.status.${value}`),
        ),
        errors: data.errors,
        reportInfo: {
            reason: handleNil(reportInfo?.reason, (value: string) =>
                t(`dictionaries.products.blockingReason.${value}`),
            ),
            scopes: handleNil(
                reportInfo?.scopes?.length ? reportInfo.scopes : null,
                (scopes: BlockingScopes[]) =>
                    scopes
                        .map((scope) => t(`dictionaries.products.blockingScope.${scope}`))
                        .join(', '),
            ),
            operator: handleNil(reportInfo?.operator),
            organization: handleNil(reportInfo?.organization),
            supportingDocumentNumber: reportInfo?.supportingDocumentNumber || null,
            supportingDocumentDate: reportInfo?.supportingDocumentDate
                ? formatDate(reportInfo.supportingDocumentDate, 'dd.MM.yyyy')
                : null,
            sourceFormation: handleNil(
                reportInfo?.formationProcess ?? reportInfo?.sourceFormation,
                (value: string) =>
                    reportInfo?.formationProcess
                        ? t(`pages.documents.blocking.formationProcess.${value}`)
                        : t(
                              `dictionaries.products.blockingSourceFormation.${value as BlockingSourceFormation}`,
                          ),
            ),
            comment: reportInfo?.comment || null,
            attachment: reportInfo?.attachment ?? [],
        },
    };
}

const GeneralInfo = ({data}: BlockingProps) => {
    const {t} = useTranslation();
    const copy = useClipboard();
    const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
    const convertedData = useMemo(() => convertData(data, t), [data, t]);
    const fields = [
        {key: 'documentDate', value: convertedData.createdAt},
        {key: 'reason', value: convertedData.reportInfo.reason},
        {key: 'scope', value: convertedData.reportInfo.scopes},
        {key: 'operator', value: convertedData.reportInfo.operator},
        {key: 'organization', value: convertedData.reportInfo.organization},
        {
            key: 'supportingDocumentNumber',
            value: convertedData.reportInfo.supportingDocumentNumber,
        },
        {key: 'supportingDocumentDate', value: convertedData.reportInfo.supportingDocumentDate},
        {key: 'sourceFormation', value: convertedData.reportInfo.sourceFormation},
        {key: 'comment', value: convertedData.reportInfo.comment},
    ].filter(({key, value}) => {
        if (
            key === 'supportingDocumentNumber' ||
            key === 'supportingDocumentDate' ||
            key === 'comment'
        ) {
            return Boolean(value);
        }

        return true;
    });

    const copySupportingDocumentNumber = () => {
        const {supportingDocumentNumber} = convertedData.reportInfo;

        if (!supportingDocumentNumber) {
            return;
        }

        copy(supportingDocumentNumber);
        toast.success(t('pages.documents.details.idCopied'));
    };

    const downloadAttachment = async (attachment: string) => {
        const fileName = getFileName(attachment);

        try {
            setDownloadingFile(attachment);
            const blob = await downloadDocumentFile(convertedData.reportId, fileName);
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = objectUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            toast.error(t('pages.documents.details.Blocking.downloadError'));
        } finally {
            setDownloadingFile(null);
        }
    };

    return (
        <GeneralInfoContent>
            {!!convertedData.errors?.length && (
                <ErrorsWrapper>
                    {convertedData.errors.map((err, index) => (
                        <Error key={index}>{err.message ?? ''}</Error>
                    ))}
                </ErrorsWrapper>
            )}
            <InfoList>
                {fields.map(({key, value}) => (
                    <InfoListRow key={key}>
                        <InfoListLabel>
                            {t(`pages.documents.details.Blocking.${key}`)}
                        </InfoListLabel>
                        <InfoListValueRow>
                            <InfoListValue italic={key === 'comment'}>{value}</InfoListValue>
                            {key === 'supportingDocumentNumber' &&
                                convertedData.reportInfo.supportingDocumentNumber && (
                                    <InlineCopyButton
                                        icon={
                                            <Icon
                                                source={CopyIcon}
                                                width={16}
                                                height={16}
                                                fillColor={colors.brandPrimary}
                                            />
                                        }
                                        onClick={copySupportingDocumentNumber}
                                        hoverIndication
                                        label={t(
                                            'pages.documents.details.Blocking.copySupportingDocumentNumber',
                                        )}
                                        labelPlace="bottom"
                                    />
                                )}
                        </InfoListValueRow>
                    </InfoListRow>
                ))}
            </InfoList>
            {!!convertedData.reportInfo.attachment?.length && (
                <>
                    <AttachmentsHeading>
                        {t('pages.documents.details.Blocking.attachments')}
                    </AttachmentsHeading>
                    <AttachmentsGrid>
                        {convertedData.reportInfo.attachment.map((attachment) => {
                            const fileName = getFileName(attachment);
                            const extension = getFileExtension(fileName);

                            return (
                                <AttachmentCard key={attachment}>
                                    <AttachmentFileIcon
                                        background={
                                            FILE_ICON_COLORS[extension] || colors.textSecondary
                                        }
                                    >
                                        {extension || 'file'}
                                    </AttachmentFileIcon>
                                    <AttachmentFileMeta>
                                        <AttachmentFileName title={fileName}>
                                            {fileName}
                                        </AttachmentFileName>
                                    </AttachmentFileMeta>
                                    <AttachmentDownloadButton
                                        type="button"
                                        disabled={downloadingFile !== null}
                                        onClick={() => downloadAttachment(attachment)}
                                        aria-label={t(
                                            'pages.documents.details.Blocking.downloadFile',
                                        )}
                                        title={t('pages.documents.details.Blocking.downloadFile')}
                                    >
                                        <Icon
                                            source={DownloadIcon}
                                            width={14}
                                            height={14}
                                            strokeColor={colors.brandPrimary}
                                            fillColor="transparent"
                                        />
                                    </AttachmentDownloadButton>
                                </AttachmentCard>
                            );
                        })}
                    </AttachmentsGrid>
                </>
            )}
        </GeneralInfoContent>
    );
};

export const Blocking = ({data}: BlockingProps) => {
    const {t} = useTranslation();
    const copy = useClipboard();
    const [step, setStep] = useState(0);
    const convertedData = useMemo(() => convertData(data, t), [data, t]);

    const copyCode = () => {
        copy(convertedData.reportId);
        toast.success(t('pages.documents.details.idCopied'));
    };

    return (
        <>
            <TitleWrapper>
                <HeaderLeft>
                    <TitleText>
                        <Title>
                            {t('pages.documents.details.Blocking.BlockingId')}{' '}
                            <Id>{convertedData.reportId}</Id>
                        </Title>
                        <CopyButton
                            icon={
                                <Icon
                                    source={CopyIcon}
                                    width={15}
                                    height={15}
                                    fillColor={colors.brandPrimary}
                                />
                            }
                            onClick={copyCode}
                            hoverIndication
                            label={t('pages.documents.details.copyId')}
                            labelPlace="bottom"
                        />
                    </TitleText>
                    <StatusWrapper color={DOCUMENT_STATUS_COLORS[data.status ?? 'DEFAULT']}>
                        {convertedData.status}
                    </StatusWrapper>
                </HeaderLeft>
            </TitleWrapper>

            <Stepper
                steps={[
                    {
                        label: t('pages.documents.detail.commonInfo'),
                        component: <GeneralInfo data={data} />,
                    },
                    {
                        label: t('pages.documents.detail.products'),
                        component: <Products reportId={convertedData.reportId} />,
                    },
                ]}
                activeStep={step}
                isClickable
                clickCallback={setStep}
            />
        </>
    );
};
